import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideBlockList,
} from "../GuideBlocks.js";

const DifficultConversationsSection = ({ heading, accentIndex = 1, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="difficult-conversations"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      You do not avoid difficult conversations because you lack courage. You avoid them because you
      have not separated what actually happened from what you are afraid it means — and walking
      into that fog feels riskier than staying silent.
    </GuideLead>
    <p className="guide-panel__p">
      <strong>Difficult conversations</strong> get easier with preparation, not with more nerve.
    </p>
    <GuideSectionLabel>Why we put them off</GuideSectionLabel>
    <p className="guide-panel__p">
      Most avoided conversations are not avoided because of the topic. They are avoided because of
      the Interpretation attached to the topic — the fear of what saying it out loud will confirm,
      or what the other person&apos;s reaction might mean.
    </p>
    <p className="guide-panel__p">
      The conversation about a missed deadline is rarely just about the deadline. It is about
      whether you are respected, whether the relationship can hold honesty, whether things will get
      worse before they get better.
    </p>
    <GuideSectionLabel>Preparing with EIER</GuideSectionLabel>
    <p className="guide-panel__p">
      Using EIER — <strong>Event → Interpretation → Evidence → Response</strong> — before a hard
      conversation changes what you walk in with.
    </p>
    <GuideBlockList
      items={[
        "Event: What specifically happened? Say it the way a neutral witness would.",
        "Interpretation: What am I assuming this means about them, about me, about the relationship? Is that the only reasonable read?",
        "Evidence: What supports that read, and what would I need to see before treating it as fact?",
        "Response: Given all three clearly seen, what do I actually want to say — and what outcome am I hoping for?",
      ]}
    />
    <GuideCallout>
      Most difficult conversations go badly not because the Event was hard to raise, but because an
      unexamined Interpretation leaked into the delivery.
    </GuideCallout>
    <GuideSectionLabel>Making the conversation easier</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="clarity"
        title="Open with the Event"
        description='"You were 20 minutes late to the last three calls" lands differently than "You do not respect my time."'
      />
      <GuideCard
        iconKey="communication"
        title="Name your experience without weaponizing it"
        description='"I felt pressure when the deadline slipped" opens a conversation. "You made this impossible" often closes one.'
      />
      <GuideCard
        iconKey="thinking"
        title="Decide your outcome before you start"
        description="Acknowledgment, a change in behavior, or simply being heard? Walking in without knowing makes it easy to escalate."
      />
      <GuideCard
        iconKey="awareness"
        title="Expect different Interpretations"
        description="The conversation is not about proving your version — it is about comparing two reads of the same Event."
      />
    </GuideCardGrid>
    <GuideCallout variant="amber">
      The conversation you are avoiding is rarely as dangerous as the story you have built around
      it.
    </GuideCallout>
  </GuidePanel>
);

export default DifficultConversationsSection;
